/**
 * Scroll to Top Plugin
 * 
 * This plugin ensures that pages always start at the top (0,0) position
 * on load/navigation, preventing unwanted auto-scrolling to bottom.
 * 
 * Implements multiple failsafes:
 * 1. Immediate scroll on navigation
 * 2. Early scroll after DOM ready
 * 3. Delayed scroll after all content loads
 * 4. Router-based scroll reset
 * 5. Fallback scroll on final render
 * 
 * Created: June 2025 to fix persistent scroll-to-bottom issues
 */

import { scrollDebugger } from '~/utils/scroll-debug';

export default defineNuxtPlugin((nuxtApp) => {
  // Only run on client side
  if (process.server) return;

  let isScrollingToTop = false;
  let scrollAttempts = 0;
  const maxScrollAttempts = 5;

  // Initialize scroll debugger in development
  if (process.dev) {
    console.log('[ScrollToTop] Development mode - initializing scroll debugger');
    setTimeout(() => {
      scrollDebugger.startLogging();
    }, 1000);
  }

  /**
   * Comprehensive scroll to top function with multiple methods
   */
  const scrollToTop = (method = 'unknown') => {
    if (isScrollingToTop) return;
    
    isScrollingToTop = true;
    scrollAttempts++;
    
    console.log(`[ScrollToTop] Attempt ${scrollAttempts} via ${method}`);
    
    try {
      // Method 1: Direct window scroll (most reliable)
      window.scrollTo(0, 0);
      
      // Method 2: Document element scroll
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;
      }
      
      // Method 3: Body scroll
      if (document.body) {
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
      }
      
      // Method 4: Force via CSS scroll-behavior temporarily
      const originalBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      
      // Restore original scroll behavior after a moment
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = originalBehavior;
      }, 50);
      
      console.log(`[ScrollToTop] Completed scroll to top via ${method}`);
      
    } catch (error) {
      console.warn(`[ScrollToTop] Error during scroll via ${method}:`, error);
    } finally {
      setTimeout(() => {
        isScrollingToTop = false;
      }, 100);
    }
  };

  /**
   * Reset scroll position with retry mechanism
   */
  const resetScrollWithRetry = (method = 'unknown') => {
    scrollToTop(method);
    
    // Retry mechanism to ensure scroll took effect
    setTimeout(() => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      
      if (currentScrollY > 10 && scrollAttempts < maxScrollAttempts) {
        console.log(`[ScrollToTop] Still at position ${currentScrollY}, retrying...`);
        resetScrollWithRetry(method + '-retry');
      }
    }, 50);
  };

  // IMMEDIATE: Scroll to top as soon as plugin loads
  resetScrollWithRetry('plugin-init');

  // EARLY: Scroll when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      resetScrollWithRetry('dom-ready');
    });
  } else {
    // DOM is already ready
    resetScrollWithRetry('dom-already-ready');
  }

  // ROUTER: Handle route changes using Nuxt's navigation
  // Use navigateTo watcher to detect route changes
  let lastRoute = '';
  const checkRouteChange = () => {
    if (typeof window !== 'undefined' && window.location.pathname !== lastRoute) {
      lastRoute = window.location.pathname;
      scrollAttempts = 0;
      resetScrollWithRetry('route-change');
      
      // Delayed scroll to catch any async content after route change
      setTimeout(() => {
        resetScrollWithRetry('route-change-delayed');
      }, 150);
    }
  };

  // Monitor for route changes
  setInterval(checkRouteChange, 100);

  // WINDOW LOAD: Final failsafe when everything is loaded
  window.addEventListener('load', () => {
    resetScrollWithRetry('window-load');
  });

  // VISIBILITY CHANGE: Reset scroll when tab becomes visible
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(() => {
        resetScrollWithRetry('visibility-change');
      }, 50);
    }
  });

  // NUXT HOOKS: Use Nuxt's lifecycle hooks
  nuxtApp.hook('page:start', () => {
    scrollAttempts = 0;
    resetScrollWithRetry('page-start');
  });

  nuxtApp.hook('page:finish', () => {
    resetScrollWithRetry('page-finish');
    
    // Final delayed attempt after page is fully rendered
    setTimeout(() => {
      resetScrollWithRetry('page-finish-delayed');
    }, 200);
  });

  // OBSERVER: Watch for DOM changes that might affect scroll
  if (typeof MutationObserver !== 'undefined') {
    let observerTimer: NodeJS.Timeout;
    
    const observer = new MutationObserver(() => {
      // Debounce the observer to avoid excessive calls
      clearTimeout(observerTimer);
      observerTimer = setTimeout(() => {
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (currentScrollY > 100) {
          resetScrollWithRetry('mutation-observer');
        }
      }, 500);
    });

    // Start observing when document body is ready
    const startObserving = () => {
      if (document.body) {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: false
        });
        console.log('[ScrollToTop] Started DOM mutation observer');
      } else {
        setTimeout(startObserving, 100);
      }
    };

    startObserving();
  }

  // GLOBAL UTILITY: Provide global scroll to top function
  if (typeof window !== 'undefined') {
    (window as any).forceScrollToTop = () => {
      scrollAttempts = 0;
      resetScrollWithRetry('manual-force');
    };
  }

  console.log('[ScrollToTop] Plugin initialized with comprehensive scroll-to-top behavior');
});
